import styled from '@emotion/styled';
import { colors, Text, Tooltip } from '@innovationdepartment/proxima-ui';
import { capitalize, type Dictionary } from 'lodash';
import type {
  AudienceTableCellRendererProps,
  LookalikeAudienceSchema,
  LookalikeAudienceStatus,
  SeedAudienceTableRowProps
} from 'types/audiences';
import VerticalWrapper from './AudienceRowCell.Wrapper';
import { status as statusUtils } from './utils';

const StatusWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StatusIcon = styled.div<{ color: string }>`
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const StatusPlus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusMore = styled.div`
  user-select: none;
  padding: 0 4px;
  border-radius: 16px;
  background-color: ${colors.neutral10};
`;

const Status = ({ count, status }: { count: number; status: string }) => {
  let color = statusUtils.color.default;

  // @ts-expect-error
  if (status in statusUtils.color) color = statusUtils.color[status];

  return (
    <StatusWrapper>
      <StatusIcon color={color}>
        <Text variant="tag2Medium" color="neutral80">
          {count}
        </Text>
      </StatusIcon>
      <Text variant="tag2Medium" color="neutral80">
        {capitalize(status)}
      </Text>
    </StatusWrapper>
  );
};

const MoreStatus = ({
  statuses,
  group
}: {
  statuses: string[];
  group: Dictionary<LookalikeAudienceSchema[]>;
}) => {
  if (statuses.length === 0) return null;

  const moreStatuses = (
    <VerticalWrapper>
      {statuses.map(status => (
        <Text
          // @ts-expect-error
          color={statusUtils.color[status] ?? statusUtils.color.default}
          key={status}
          variant="body3Link"
        >{`${group[status].length} ${capitalize(status)}`}</Text>
      ))}
    </VerticalWrapper>
  );

  return (
    <Tooltip title={moreStatuses} placement="right" arrow>
      <StatusMore>
        <Text variant="tag2Medium" color="neutral100">{`+${statuses.length}`}</Text>
      </StatusMore>
    </Tooltip>
  );
};

// TODO(Jenky): requires tests
const AudienceRowCellStatus = ({
  row
}: AudienceTableCellRendererProps<SeedAudienceTableRowProps>) => {
  const group = statusUtils.group(row?.lookalikeAudiences ?? []);
  const sortedStatus = statusUtils.sort(Object.keys(group) as LookalikeAudienceStatus[]);

  const errorsToShow = sortedStatus.slice(0, statusUtils.limit);
  const errorsForTooltip = sortedStatus.slice(statusUtils.limit);
  // incase of multiple status count, avoiding the tooltip as it may conflict with moreStatus
  // moreover grouping is applied for lookalike status and when status of it is in error
  // the system will show the error code for each individual ones when row is expanded
  const hasMultiple = sortedStatus.length > 1;

  return (
    <VerticalWrapper gap={8}>
      {errorsToShow.map((status, i) => (
        <StatusPlus key={status}>
          <Tooltip title={hasMultiple ? '' : row?.errorCode || ''} placement="right" arrow>
            <Status key={status} count={group[status].length} status={status} />
            {i === statusUtils.limit - 1 && (
              <MoreStatus group={group} statuses={errorsForTooltip} />
            )}
          </Tooltip>
        </StatusPlus>
      ))}
    </VerticalWrapper>
  );
};

export default AudienceRowCellStatus;
